import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Plane, Download } from "lucide-react";
import { GOOGLE_FORM_URL } from "@/constants/links";

const LINES = ["Your Wings", "to Global", "Education."];

const container = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const line = {
  hidden: { y: "115%" },
  show: {
    y: "0%",
    transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const chipY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#FDFDFD] pt-20 md:pt-24"
      data-testid="hero-section"
    >
      {/* Grid backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #0a192f 1px, transparent 1px), linear-gradient(to bottom, #0a192f 1px, transparent 1px)",
          backgroundSize: "88px 88px",
        }}
      />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 pt-8 md:pt-14 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
        {/* Left content */}
        <motion.div
          style={{ y: textY }}
          className="md:col-span-7 flex flex-col justify-between"
        >
          <motion.div
            style={{ y: chipY }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex items-center gap-3 mb-8 md:mb-12"
          >
            <span className="chip">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00A3FF] animate-pulse" />
              Est. 2015 · Study Abroad Studio
            </span>
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-medium tracking-[-0.035em] leading-[0.92] text-[#0a192f] text-[13vw] sm:text-[10vw] md:text-[8.2vw] lg:text-[7.6vw]"
          >
            {LINES.map((l, i) => (
              <span key={i} className="mask-line">
                <motion.span variants={line} className="mask-line-inner">
                  {l === "Global" ? (
                    <>
                      to <span className="italic font-normal">Global</span>
                    </>
                  ) : (
                    l
                  )}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
          >
            <p className="md:col-span-6 text-[#0a192f]/70 text-base md:text-lg font-body leading-relaxed max-w-md">
              Edufly Consultancy guides you through university applications,
              visa processing, and scholarships to land your dream future
              abroad.
            </p>
            <div className="md:col-span-6 flex flex-wrap items-center gap-3">
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill btn-navy"
                data-testid="hero-cta-primary"
              >
                Book a Free Consultation
                <ArrowUpRight size={16} />
              </a>
              <a
                href="#destinations"
                className="btn-pill btn-outline-navy"
                data-testid="hero-cta-secondary"
              >
                Explore Countries
              </a>
              <a
                href="/edufly-brochure.pdf"
                download="Edufly-Brochure.pdf"
                className="btn-pill btn-outline-navy"
                data-testid="hero-cta-brochure"
              >
                Download Brochure
                <Download size={16} />
              </a>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
          className="md:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden clip-notched bg-[#0a192f]">
            <motion.div
              style={{ y: imageY, scale: imageScale }}
              className="absolute inset-0 will-change-transform"
            >
              <img
                src="https://images.pexels.com/photos/7683728/pexels-photo-7683728.jpeg"
                alt="Students at modern international university campus"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0a192f]/70 via-[#0a192f]/20 to-transparent" />

            <div className="absolute top-4 left-4 right-4 flex items-center justify-between font-mono-tech text-white/85 text-[11px] tracking-wide">
              <span>◈ EDUFLY / 2025</span>
              <span>N 51.5074° W 0.1278°</span>
            </div>

            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div className="text-white">
                <div className="font-mono-tech text-[10px] opacity-70 mb-1">
                  FEATURED · UNITED KINGDOM
                </div>
                <div className="font-display text-xl md:text-2xl leading-tight max-w-[220px]">
                  Departure logged. Destination: your future.
                </div>
              </div>
              <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-[#00A3FF]">
                <Plane size={16} className="text-white -rotate-45" />
              </div>
            </div>
          </div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="hidden md:flex absolute -left-16 bottom-16 bg-white border border-[#0a192f]/10 shadow-[0_20px_60px_-20px_rgba(10,25,47,0.35)] rounded-xl p-5 w-56"
          >
            <div>
              <div className="font-mono-tech text-[10px] opacity-60 mb-1.5">
                LIVE COUNTER
              </div>
              <div className="font-display text-4xl font-bold text-[#0a192f] leading-none">
                12,480+
              </div>
              <div className="text-[11px] text-[#0a192f]/60 mt-2 leading-snug">
                Students placed at partner universities worldwide.
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom info bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative border-t border-[#0a192f]/10 py-5 px-6 md:px-10 mx-auto max-w-[1440px] grid grid-cols-2 md:grid-cols-4 gap-6 font-mono-tech text-[11px] text-[#0a192f]/70"
      >
        <div className="flex flex-col">
          <span className="opacity-50">01 / VISA SUCCESS</span>
          <span className="text-[#0a192f] font-semibold">99.2%</span>
        </div>
        <div className="flex flex-col">
          <span className="opacity-50">02 / UNIVERSITIES</span>
          <span className="text-[#0a192f] font-semibold">50+ PARTNERS</span>
        </div>
        <div className="flex flex-col">
          <span className="opacity-50">03 / COUNTRIES</span>
          <span className="text-[#0a192f] font-semibold">PL · DE · UK · IE · EU</span>
        </div>
        <div className="flex flex-col">
          <span className="opacity-50">04 / OFFICES</span>
          <span className="text-[#0a192f] font-semibold">BAREILLY · WARSAW</span>
        </div>
      </motion.div>
    </section>
  );
}
